import { AlertTriangle, Loader2 } from 'lucide-react';
import type { GuestCommunicationChannel, PropertyCommunicationPreviewResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { Lang } from './editorModel';
import { markValues, previewDocument } from './previewHighlight';

interface Props {
  channel: GuestCommunicationChannel;
  preview: PropertyCommunicationPreviewResponse | null;
  updating: boolean;
  reason: string | null;
  /** Der Server hat diese Fassung nicht erzeugt — anders als ein Befund am Feld. */
  failed: boolean;
  /** Nur wo ein zweiter Versuch etwas ändern kann: dieselbe Fassung noch einmal anfragen. */
  retry: (() => void) | null;
  draft: boolean;
  values: string[];
  guestLang: Lang;
  onGuestLangChange: (lang: Lang) => void;
  /** Höhe des E-Mail-Rahmens — im Vollbild darf er wachsen. */
  frameClass?: string;
}

/**
 * Was der Gast bekommt, so wie der Server es erzeugt: Gastsprache zuerst, eingesetzte Werte
 * markiert, Beispielwerte statt Gastdaten.
 */
export function MessagePreview(props: Props) {
  const rendered = props.guestLang === 'De' ? props.preview?.german : props.preview?.english;
  return (
    <section aria-label="Vorschau" className="border-line/50 bg-card shadow-card rounded-lg border">
      <div className="border-line/60 flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <h4 className="text-ink font-serif text-lg">Vorschau</h4>
        <div
          role="group"
          aria-label="Sprache des Gasts"
          className="bg-card-alt/60 inline-flex rounded-lg p-1"
        >
          {(['De', 'En'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              aria-pressed={props.guestLang === lang}
              onClick={() => props.onGuestLangChange(lang)}
              className={cn(
                'focus-visible:outline-sage min-h-11 rounded-md px-3 text-xs font-semibold focus-visible:outline focus-visible:outline-2',
                props.guestLang === lang ? 'bg-navy text-paper' : 'text-ink-soft hover:text-ink',
              )}
            >
              Gast {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <p className="border-line/60 text-ink-soft flex flex-wrap justify-between gap-x-3 border-b px-4 py-2 text-xs">
        <span className="inline-flex items-center gap-1.5" aria-live="polite">
          {props.updating && <Loader2 size={12} className="motion-safe:animate-spin" aria-hidden />}
          {props.updating
            ? rendered
              ? 'Wird aktualisiert … letzte Fassung bleibt sichtbar'
              : 'Wird erzeugt …'
            : `${props.draft ? 'Entwurf' : 'Gespeicherte Fassung'} · vom Server erzeugt`}
        </span>
        <span>Beispielwerte</span>
      </p>
      {props.reason && (
        <div
          role="status"
          className="border-amber/30 bg-amber/10 text-ink flex items-start gap-2 border-b px-4 py-2 text-xs"
        >
          <AlertTriangle size={13} className="text-amber mt-px shrink-0" aria-hidden />
          <p className="min-w-0 flex-1">
            {rendered ? 'Zeigt die letzte gültige Fassung' : 'Noch keine Vorschau'} – {props.reason}
          </p>
          {props.retry && (
            <button
              type="button"
              onClick={props.retry}
              className="text-ink focus-visible:outline-sage -my-3 min-h-11 shrink-0 font-semibold underline underline-offset-2 focus-visible:outline focus-visible:outline-2"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      )}
      <div className="p-3">
        {!rendered ? (
          <p className="text-ink-soft py-10 text-center text-sm">
            {props.failed
              ? 'Die Vorschau konnte nicht erzeugt werden.'
              : props.reason
                ? 'Sobald die Fassung gültig ist, erscheint sie hier.'
                : 'Vorschau wird erzeugt …'}
          </p>
        ) : props.channel === 'Email' ? (
          <div className="border-line/60 overflow-hidden rounded-lg border bg-white">
            <p className="border-line/60 text-ink-soft border-b px-4 py-2 text-xs">
              An: Max (Beispielgast) · Manuelle Buchung
            </p>
            {rendered.subject && (
              <p className="text-ink px-4 pt-3 text-sm font-semibold">
                <Marked text={rendered.subject} values={props.values} />
              </p>
            )}
            <iframe
              title={`E-Mail-Vorschau ${props.guestLang === 'De' ? 'Deutsch' : 'Englisch'}`}
              sandbox=""
              srcDoc={previewDocument(rendered.html ?? '', props.values)}
              className={cn('block w-full', props.frameClass ?? 'h-[34rem]')}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-ink-soft text-xs">Nachricht im Buchungsportal an Max</p>
            <p className="bg-card-alt/60 text-ink whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
              <Marked text={rendered.body ?? ''} values={props.values} />
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function Marked({ text, values }: { text: string; values: string[] }) {
  return (
    <>
      {markValues(text, values).map((piece, index) =>
        piece.value ? (
          <mark key={index} className="bg-card-alt text-ink rounded-sm px-0.5">
            {piece.text}
          </mark>
        ) : (
          <span key={index}>{piece.text}</span>
        ),
      )}
    </>
  );
}
